import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import pitchAerial from "@/assets/pitch-aerial.jpg";
import frxHero from "@/assets/frx-hero.webp";
import { getProductImages } from "@/lib/productImages";
import { uploadedImageUrl } from "@/components/ImageUploader";
import { CommercialCard } from "@/components/CommercialCard";
import { CommercialMaintenance } from "@/components/CommercialMaintenance";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { Building2, TreePine, GraduationCap, Landmark, ClipboardCheck, ShieldCheck } from "lucide-react";

const APPLICATIONS = [
  { icon: Building2, title: "Business parks & campuses", copy: "Consistent, autonomous mowing for large corporate grounds." },
  { icon: TreePine, title: "Estates & amenity grounds", copy: "Reliable coverage for parks, estates and public spaces." },
  { icon: GraduationCap, title: "Schools & universities", copy: "Low-noise operation suited to education settings." },
  { icon: Landmark, title: "Hotels & resorts", copy: "Pristine grounds with minimal disruption to guests." },
];

const PROCESS = [
  { icon: ClipboardCheck, title: "Site survey", copy: "We assess your grounds, access and coverage needs." },
  { icon: ShieldCheck, title: "Tailored recommendation", copy: "Machinery matched to your site — no prices until we understand the job." },
  { icon: Building2, title: "Installation & support", copy: "Professional setup with priority servicing packages." },
];

export default function Commercial() {
  const [products, setProducts] = useState([]);
  const [enq, setEnq] = useState({ open: false, product: null });

  useEffect(() => {
    api.get("/products", { params: { category: "commercial" } })
      .then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const openEnquiry = (product) => setEnq({ open: true, product });

  return (
    <div>
      <section className="relative bg-brand-ink text-white">
        <div className="absolute inset-0 opacity-45"><img src={frxHero} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/70 to-brand-ink/30" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-white">Commercial Mowing</span>
          <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Autonomous mowing for large sites</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">Reliable, efficient robotic mowing for estates, parks, campuses and amenity grounds — with expert guidance, installation and priority support. Every commercial solution is enquiry-led.</p>
          <Button onClick={() => setEnq({ open: true, product: null })} className="mt-8 bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="commercial-hero-enquire">Discuss your project</Button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Commercial range</h2>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Details and specifications for our commercial machines are being finalised. Contact us and we'll advise on the right solution for your site.</p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => <CommercialCard key={p.slug} product={p} image={(p.uploaded_images?.[0] && uploadedImageUrl(p.uploaded_images[0])) || getProductImages(p.slug)[0] || IMAGES.commercial} onEnquire={openEnquiry} />)}
        </div>
      </section>

      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Where it works</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {APPLICATIONS.map((a) => (
              <div key={a.title} className="rounded-2xl border border-brand-navy/10 p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><a.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{a.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{a.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">How commercial projects work</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROCESS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <div className="flex items-center gap-3"><span className="grid place-items-center h-10 w-10 rounded-lg bg-brand-navy/10 text-brand-navy"><s.icon className="h-5 w-5" /></span><span className="font-mono-spec text-sm text-brand-navy">0{i + 1}</span></div>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.copy}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-3xl bg-brand-navy text-white p-8 md:p-12 text-center">
          <h3 className="font-display font-bold text-2xl">Tell us about your site</h3>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">Share a few details and our team will arrange a site survey to recommend the right commercial solution.</p>
          <Button onClick={() => setEnq({ open: true, product: null })} className="mt-6 bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="commercial-cta-enquire">Request a site survey</Button>
        </div>
      </section>

      <CommercialMaintenance onEnquire={() => setEnq({ open: true, product: null })} />

      <EnquiryDialog open={enq.open} onOpenChange={(o) => setEnq((e) => ({ ...e, open: o }))} type="commercial" product={enq.product} sourcePage="commercial" />
    </div>
  );
}
